import { lazy, Suspense } from "react";
import AuthGuard from "../components/authentication/auth-guard.js";
import AdminProtectedRoute from "../components/authentication/admin-protected-route.js";
import { DashboardLayout } from "../components/dashboard/dashboard-layout.js";

// ─── lazyWithPreload : lazy() + méthode .preload() pour déclencher le download
// du chunk JS depuis la sidebar avant le clic de l'utilisateur.
function lazyWithPreload(factory) {
  const Component = lazy(factory);
  Component.preload = factory;
  return Component;
}

// ─── Lazy loading : chaque page est chargée uniquement quand elle est visitée ──
const Login                      = lazyWithPreload(() => import("./login_page/index.js"));
const Tenant_page                = lazyWithPreload(() => import("./tenant_page/index.js"));
const Dashboard                  = lazyWithPreload(() => import("../pages/dashboard/index.js"));
const Customers                  = lazyWithPreload(() => import("../pages/dashboard/customers/index.js"));
const Audits                     = lazyWithPreload(() => import("../pages/dashboard/audits/index.js"));
const Agenda                     = lazyWithPreload(() => import("../pages/dashboard/agenda/index.js"));
const Tcs                        = lazyWithPreload(() => import("../pages/dashboard/tcs/index.js"));
const Errors                     = lazyWithPreload(() => import("../pages/dashboard/errors.js"));
const CreateUser                 = lazyWithPreload(() => import("../pages/dashboard/create/index.js"));
const CustomerDetails            = lazyWithPreload(() => import("../pages/dashboard/customers/[customerId].js"));
const AuditsGraphicsDetails      = lazyWithPreload(() => import("../pages/dashboard/audits/[auditId].js"));
const CourseSession              = lazyWithPreload(() => import("../pages/dashboard/session/[session].js"));
const CreateSession              = lazyWithPreload(() => import("../pages/dashboard/session/create_session.js"));
const Forum                      = lazyWithPreload(() => import("../pages/dashboard/forum/index.js"));
const SessionHome                = lazyWithPreload(() => import("../pages/dashboard/session/home.js"));
const Formations                 = lazyWithPreload(() => import("../pages/dashboard/formations/index.js"));
const FormationsDetails          = lazyWithPreload(() => import("../pages/dashboard/formations/[formationId].js"));
const TcsInformationsById        = lazyWithPreload(() => import("../pages/dashboard/tcs/[tcs].js"));
const Facturation                = lazyWithPreload(() => import("../pages/dashboard/facturation/index.js"));
const Activities                 = lazyWithPreload(() => import("../pages/dashboard/activities/index.js"));
const Parameters                 = lazyWithPreload(() => import("../pages/dashboard/parameters/index.js"));
const FeedBacks                  = lazyWithPreload(() => import("../pages/dashboard/FeedBacks/index.js"));
const FeedBackByCustomerId       = lazyWithPreload(() => import("../pages/dashboard/FeedBacks/feedBackByCustomerId.js"));
const CreateAdmin                = lazyWithPreload(() => import("../pages/dashboard/create/index-admin.js"));
const PatientsFolder             = lazyWithPreload(() => import("../pages/dashboard/patients_folder/index.js"));
const PatientsFolderDetails      = lazyWithPreload(() => import("../pages/dashboard/patients_folder/[patients_folder].js"));
const Satisfaction               = lazyWithPreload(() => import("../pages/dashboard/qualite/Seatisfaction.js"));
const SurveyDetails              = lazyWithPreload(() => import("../pages/dashboard/qualite/[surveyId].js"));
const FormationQuestionnairePage = lazyWithPreload(() => import("../pages/dashboard/formation-questionnaire/index.js"));
const Discussion                 = lazyWithPreload(() => import("../pages/dashboard/discussion/index.js"));
const StatistiquesAudit          = lazyWithPreload(() => import("../pages/dashboard/statistiques_audit/index.js"));
const NotFound                   = lazyWithPreload(() => import("../pages/NotFound.js"));
const Email_sent                 = lazyWithPreload(() => import("../pages/email_sent.js"));
const ChangePasswordFirstLogin   = lazyWithPreload(() => import("../pages/confrm_email.js"));
const NotAvailable               = lazyWithPreload(() => import("../pages/NotAvailable.js"));
const Score                      = lazyWithPreload(() => import("../components/dashboard/scores/Score.js"));
const FormateurHome              = lazyWithPreload(() => import("../pages/dashboard/formateurs/index.js"));
const FormateurProfile           = lazyWithPreload(() => import("../pages/dashboard/formateurs/[formateur].js"));
const RoleAgendadRedirect        = lazyWithPreload(() => import("./roles_redirection/agenda-role-redirection.js"));
const RoleCustomersdRedirect     = lazyWithPreload(() => import("./roles_redirection/customers-role-redirection.js"));
const RoleCustomerDetailsdRedirect = lazyWithPreload(() => import("./roles_redirection/customer-details-role-redirection.js"));
const Compte                     = lazyWithPreload(() => import("../pages/dashboard/Reglages/compte/compte.js"));
const Extention                  = lazyWithPreload(() => import("../pages/dashboard/Reglages/Extensions/extensions.js"));
const Personnalisation           = lazyWithPreload(() => import("../pages/dashboard/Reglages/Personnalisation/personnalisation.js"));
const CreateTenantPage           = lazyWithPreload(() => import("../pages/dashboard/tenants/create_tenant.js"));
const DetailedResultsPage        = lazyWithPreload(() => import("../components/dashboard/DetailedResultsPage.js"));
const PermissionsTree            = lazyWithPreload(() => import("../pages/dashboard/Reglages/Permission/PermissionsTree.js"));
const RolesTable                 = lazyWithPreload(() => import("../pages/dashboard/Reglages/Role/Role_table.js"));
const PermissionsTre             = lazyWithPreload(() => import("../pages/dashboard/Reglages/Permission_Role/PermissionsTree.js"));
const PermissionsTreee           = lazyWithPreload(() => import("../pages/dashboard/Reglages/Permissions_Edit/PermissionsTreee.js"));
const List_admin                 = lazyWithPreload(() => import("../components/dashboard/create/list-admin.js"));
const AdminUpdateForm            = lazyWithPreload(() => import("../components/dashboard/create/adminUpdateForm.js").then(m => ({ default: m.AdminUpdateForm })));
const Imports                    = lazyWithPreload(() => import("../pages/dashboard/import/Imports.js"));
const GroupsPage                 = lazyWithPreload(() => import("../pages/dashboard/Groupes/groupes.js"));
const GroupDetailsPage           = lazyWithPreload(() => import("../pages/dashboard/Groupes/GroupDetailsPage.js"));

// Export des preloaders indexés par segment d'URL — utilisé par la sidebar
export const CHUNK_PRELOADERS = {
  customers:    () => { Customers.preload(); CustomerDetails.preload(); },
  'epp/audits': () => { Audits.preload(); AuditsGraphicsDetails.preload(); },
  'epp/tcs':    () => { Tcs.preload(); TcsInformationsById.preload(); },
  session:      () => { SessionHome.preload(); CourseSession.preload(); },
  activities:   () => Activities.preload(),
  parametres:   () => Parameters.preload(),
  satisfaction: () => { Satisfaction.preload(); SurveyDetails.preload(); },
  experts:      () => { FormateurHome.preload(); FormateurProfile.preload(); },
  actions:      () => { Formations.preload(); FormationsDetails.preload(); },
  group:        () => { GroupsPage.preload(); GroupDetailsPage.preload(); },
  agenda:       () => Agenda.preload(),
  forum:        () => Forum.preload(),
  discussion:   () => Discussion.preload(),
  facturation:  () => Facturation.preload(),
  scores:       () => Score.preload(),
};

// Pour les routes HORS dashboard (login, tenant…) : Suspense autonome avec fallback null.
const withSuspense = (element) => (
  <Suspense fallback={null}>{element}</Suspense>
);

// Pour les enfants du dashboard : PAS de Suspense ici.
// La Suspense est dans DashboardLayout (autour de <Outlet />).
// Cela permet à startTransition de voir la suspension et de maintenir
// l'ancien contenu visible pendant le chargement du chunk.
// Si withSuspense était utilisé ici, il attraperait la suspension en premier
// et retournerait null — React penserait que tout est résolu et commiterait un écran vide.
const dashEl = (element) => element;

const routes = [
  {
    path: '/:tenant/Email_confirmed/:token',
    element: withSuspense(<ChangePasswordFirstLogin />),
  },
  {
    path: '/:tenant/change_password/:token',
    element: withSuspense(<ChangePasswordFirstLogin />),
  },
  {
    path: '/:tenant/confirm_email',
    element: withSuspense(<Email_sent />),
  },
  {
    path: "/create_tenant",
    element: withSuspense(<CreateTenantPage />),
  },
  {
    path: "/",
    element: withSuspense(<Tenant_page />),
  },
  {
    path: "/:tenant",
    element: withSuspense(<Login />),
  },
  {
    path: "/detailed-results",
    element: withSuspense(<DetailedResultsPage />),
  },
  {
    path: `/:tenant/dashboard`,
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: dashEl(<Dashboard />),
      },
      {
        path: "agenda",
        element: dashEl(<RoleAgendadRedirect />),
      },
      {
        path: "scores",
        element: dashEl(
          <AdminProtectedRoute><Score /></AdminProtectedRoute>
        ),
      },
      {
        path: "audit-session/:auditId/:startSession/:endSession",
        element: dashEl(<StatistiquesAudit />),
      },
      {
        path: "customers",
        children: [
          {
            path: "",
            element: dashEl(<RoleCustomersdRedirect />),
          },
          {
            path: ":customerId",
            element: dashEl(<RoleCustomerDetailsdRedirect />),
          },
          {
            path: "not-available",
            element: dashEl(<NotAvailable />),
          },
        ],
      },
      {
        path: "epp/audits",
        children: [
          {
            path: "",
            element: dashEl(<Audits />),
          },
          {
            path: ":auditId",
            element: dashEl(<AuditsGraphicsDetails />),
          },
          {
            path: "not-available",
            element: dashEl(<NotAvailable origin="audits" />),
          },
        ],
      },
      {
        path: "epp/tcs",
        children: [
          {
            path: "",
            element: dashEl(<Tcs />),
          },
          {
            path: ":tcsId",
            element: dashEl(<TcsInformationsById />),
          },
        ],
      },
      {
        path: "actions",
        children: [
          {
            path: "",
            element: dashEl(
              <AdminProtectedRoute><Formations /></AdminProtectedRoute>
            ),
          },
          {
            path: ":formationId",
            element: dashEl(
              <AdminProtectedRoute><FormationsDetails /></AdminProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "satisfaction",
        children: [
          {
            path: "",
            element: dashEl(
              <AdminProtectedRoute><Satisfaction /></AdminProtectedRoute>
            ),
          },
          {
            path: ":surveyId",
            element: dashEl(
              <AdminProtectedRoute><SurveyDetails /></AdminProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "formation-questionnaire",
        element: dashEl(
          <AdminProtectedRoute><FormationQuestionnairePage /></AdminProtectedRoute>
        ),
      },
      {
        path: "session",
        children: [
          {
            path: "",
            element: dashEl(<SessionHome />),
          },
          {
            path: ":sessionId",
            element: dashEl(<CourseSession />),
          },
          {
            path: "create",
            element: dashEl(<CreateSession />),
          },
          {
            path: "not-available",
            element: dashEl(<NotAvailable origin="sessions" />),
          },
        ],
      },
      {
        path: "experts",
        children: [
          {
            path: "",
            element: dashEl(
              <AdminProtectedRoute><FormateurHome /></AdminProtectedRoute>
            ),
          },
          {
            path: ":formerId",
            element: dashEl(
              <AdminProtectedRoute><FormateurProfile /></AdminProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "errors",
        element: dashEl(
          <AdminProtectedRoute><Errors /></AdminProtectedRoute>
        ),
      },
      {
        path: "create/customers",
        element: dashEl(
          <AdminProtectedRoute><CreateUser /></AdminProtectedRoute>
        ),
      },
      {
        path: "group",
        element: dashEl(<GroupsPage />),
      },
      {
        path: "group/:groupId",
        element: dashEl(<GroupDetailsPage />),
      },
      {
        path: "forum",
        element: dashEl(<Forum />),
      },
      {
        path: "permission_role",
        element: dashEl(<PermissionsTre />),
      },
      {
        path: "permission_edit",
        element: dashEl(<PermissionsTreee />),
      },
      {
        path: "roles",
        element: dashEl(<RolesTable />),
      },
      {
        path: "import",
        element: dashEl(<Imports />),
      },
      {
        path: "discussion",
        element: dashEl(<Discussion />),
      },
      {
        path: "facturation",
        element: dashEl(
          <AdminProtectedRoute><Facturation /></AdminProtectedRoute>
        ),
      },
      {
        path: "activities",
        element: dashEl(
          <AdminProtectedRoute><Activities /></AdminProtectedRoute>
        ),
      },
      {
        path: "parametres",
        element: dashEl(
          <AdminProtectedRoute><Parameters /></AdminProtectedRoute>
        ),
      },
      {
        path: "create/admin",
        element: dashEl(
          <AdminProtectedRoute><CreateAdmin /></AdminProtectedRoute>
        ),
      },
      {
        path: "admins",
        element: dashEl(<List_admin />),
      },
      {
        path: "admins/update/:id",
        element: dashEl(<AdminUpdateForm />),
      },
      {
        path: "patients_folder",
        children: [
          {
            path: "",
            element: dashEl(
              <AdminProtectedRoute><PatientsFolder /></AdminProtectedRoute>
            ),
          },
          {
            path: ":patientsFolderId",
            element: dashEl(
              <AdminProtectedRoute><PatientsFolderDetails /></AdminProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "not-available",
        element: dashEl(<NotAvailable />),
      },
    ],
  },
  {
    path: "/not-found",
    element: withSuspense(<NotFound />),
  },
  {
    path: "*",
    element: withSuspense(<NotFound />),
  },
];

export default routes;
